/**
 * Players — live snapshot of the lobby's connected players. Pulled straight
 * from `useStore` (which mirrors the Colyseus schema). Distance is measured
 * from the local player's current xz so the table is sortable by proximity.
 */
import React, { useMemo, useState } from "react";

import { useStore, type PlayerView } from "../../store";
import { btnStyle, colors, copyToClipboard, styles } from "./_shared";

type SortKey = "name" | "id" | "distance";

export function PlayersSection(): JSX.Element {
  const localPlayer = useStore((s) => s.localPlayer);
  const remotePlayersMap = useStore((s) => s.remotePlayers);
  const [sortKey, setSortKey] = useState<SortKey>("distance");

  const players = useMemo(() => {
    const list: PlayerView[] = [];
    if (localPlayer) list.push(localPlayer);
    for (const p of remotePlayersMap.values()) list.push(p);
    if (sortKey === "name") {
      list.sort((a, b) => (a.displayName || a.id).localeCompare(b.displayName || b.id));
    } else if (sortKey === "id") {
      list.sort((a, b) => a.id.localeCompare(b.id));
    } else if (sortKey === "distance" && localPlayer) {
      list.sort((a, b) => distance(localPlayer, a) - distance(localPlayer, b));
    }
    return list;
  }, [localPlayer, remotePlayersMap, sortKey]);

  const copyTable = async () => {
    const rows = players.map((p) =>
      [
        p === localPlayer ? "LOCAL" : "remote",
        p.id,
        p.displayName || "(unnamed)",
        `${p.x.toFixed(2)},${p.y.toFixed(2)},${p.z.toFixed(2)}`,
        `rot=${p.rotY.toFixed(2)}`,
      ].join("\t"),
    );
    await copyToClipboard(rows.join("\n"));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>Players ({players.length})</div>
          <div style={styles.sectionSubtitle}>
            mirror of LobbyRoom state · {localPlayer ? "joined" : "not joined"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setSortKey("distance")} style={btnStyle({ tone: sortKey === "distance" ? "primary" : "ghost", small: true })}>
            by distance
          </button>
          <button onClick={() => setSortKey("name")} style={btnStyle({ tone: sortKey === "name" ? "primary" : "ghost", small: true })}>
            by name
          </button>
          <button onClick={() => setSortKey("id")} style={btnStyle({ tone: sortKey === "id" ? "primary" : "ghost", small: true })}>
            by id
          </button>
          <button onClick={() => void copyTable()} style={btnStyle({ tone: "ghost", small: true })}>
            Copy table
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {players.length === 0 ? (
          <div style={{ padding: 12, ...styles.mono, color: colors.textMute }}>
            (no players — open the lobby route)
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>kind</Th>
                <Th>sessionId</Th>
                <Th>displayName</Th>
                <Th>x</Th>
                <Th>y</Th>
                <Th>z</Th>
                <Th>rotY</Th>
                <Th>dist</Th>
                <Th>{""}</Th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => {
                const isLocal = p === localPlayer;
                const dist = localPlayer ? distance(localPlayer, p) : 0;
                return (
                  <tr key={p.id} style={isLocal ? { background: "rgba(74,142,255,0.06)" } : undefined}>
                    <Td>{isLocal ? "LOCAL" : "remote"}</Td>
                    <Td title={p.id}><code style={styles.mono}>{p.id.slice(0, 10)}</code></Td>
                    <Td>{p.displayName || <span style={{ color: colors.textFaint }}>(unnamed)</span>}</Td>
                    <Td>{p.x.toFixed(2)}</Td>
                    <Td>{p.y.toFixed(2)}</Td>
                    <Td>{p.z.toFixed(2)}</Td>
                    <Td>{p.rotY.toFixed(2)}</Td>
                    <Td>{isLocal ? "—" : dist.toFixed(2)}</Td>
                    <Td>
                      <button
                        onClick={() => void copyToClipboard(p.id)}
                        style={btnStyle({ tone: "ghost", small: true })}
                      >
                        Copy
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function distance(a: PlayerView, b: PlayerView): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 11,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  color: colors.text,
};

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "6px 8px",
        background: colors.bgHeader,
        borderBottom: `1px solid ${colors.border}`,
        color: colors.textFaint,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        position: "sticky",
        top: 0,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, title }: { children: React.ReactNode; title?: string }): JSX.Element {
  return (
    <td title={title} style={{ padding: "5px 8px", borderBottom: `1px solid ${colors.border}` }}>
      {children}
    </td>
  );
}

